import { showGlobalError } from '@/components/feedback';

export const enum STORAGE_KEYS {
  AUTH_TOKEN = 'auth_token',
  USER_PREFERENCES = 'user_preferences',
  LANGUAGE = 'language',
  THEME = 'theme-mode',
  TEXT_DIRECTION = 'text-direction',
  LAST_LOGIN = 'last_login',
  PWA_INSTALL_SHOWN = 'pwa_install_shown',
}

type StorageType = 'localStorage' | 'sessionStorage' | null;

interface IStorageService {
  getItem<T = string>(key: STORAGE_KEYS | string): T | null;
  setItem(key: STORAGE_KEYS | string, value: unknown): boolean;
  removeItem(key: STORAGE_KEYS | string): boolean;
  clear(): boolean;
  getAllKeys(): readonly string[];
  hasItem(key: STORAGE_KEYS | string): boolean;
  getSize(): number;
  shouldShowPWAInstall(): boolean;
  markPWAInstallShown(): void;
}

class StorageService implements IStorageService {
  private static instance: StorageService | undefined;
  private storageType: StorageType = null;
  private storage: Storage | null = null;

  private constructor() {
    this.initializeStorage();
  }

  static getInstance(): StorageService {
    return (StorageService.instance ??= new StorageService());
  }

  private initializeStorage(): void {
    if (typeof window === 'undefined') return;

    const testKey = '__storage_test__';

    // Try localStorage first
    try {
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      this.storage = localStorage;
      this.storageType = 'localStorage';
      return;
    } catch {
      // Fallback to sessionStorage
      try {
        sessionStorage.setItem(testKey, testKey);
        sessionStorage.removeItem(testKey);
        this.storage = sessionStorage;
        this.storageType = 'sessionStorage';
      } catch {
        this.storage = null;
        this.storageType = null;
      }
    }
  }

  getItem<T = string>(key: STORAGE_KEYS | string): T | null {
    if (!this.storage) return null;

    try {
      const item = this.storage.getItem(key);
      if (item === null) return null;

      // Try JSON parse, fallback to string
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as T;
      }
    } catch {
      showGlobalError(`Failed to read from storage: ${key}`);
      return null;
    }
  }

  setItem(key: STORAGE_KEYS | string, value: unknown): boolean {
    if (!this.storage) return false;

    try {
      const serializedValue =
        typeof value === 'string' ? value : JSON.stringify(value);
      this.storage.setItem(key, serializedValue);
      return true;
    } catch {
      showGlobalError(`Failed to save to storage: ${key}`);
      return false;
    }
  }

  removeItem(key: STORAGE_KEYS | string): boolean {
    if (!this.storage) return false;

    try {
      this.storage.removeItem(key);
      return true;
    } catch {
      showGlobalError(`Failed to remove from storage: ${key}`);
      return false;
    }
  }

  clear(): boolean {
    if (!this.storage) return false;

    try {
      this.storage.clear();
      return true;
    } catch {
      showGlobalError('Failed to clear storage');
      return false;
    }
  }

  getAllKeys(): readonly string[] {
    if (!this.storage) return [];

    try {
      return Object.keys(this.storage);
    } catch {
      showGlobalError('Failed to get storage keys');
      return [];
    }
  }

  hasItem(key: STORAGE_KEYS | string): boolean {
    if (!this.storage) return false;

    try {
      return this.storage.getItem(key) !== null;
    } catch {
      showGlobalError(`Failed to check storage: ${key}`);
      return false;
    }
  }

  getSize(): number {
    if (!this.storage) return 0;

    try {
      let size = 0;
      for (const key in this.storage) {
        if (Object.prototype.hasOwnProperty.call(this.storage, key)) {
          size += this.storage[key].length + key.length;
        }
      }
      return size;
    } catch {
      showGlobalError('Failed to calculate storage size');
      return 0;
    }
  }

  shouldShowPWAInstall(): boolean {
    const sessionKey = `${STORAGE_KEYS.PWA_INSTALL_SHOWN}_${Math.floor(Date.now() / 300000)}`; // 5-min intervals
    try {
      return !sessionStorage.getItem(sessionKey);
    } catch {
      return true; // Show if sessionStorage fails
    }
  }

  markPWAInstallShown(): void {
    const sessionKey = `${STORAGE_KEYS.PWA_INSTALL_SHOWN}_${Math.floor(Date.now() / 300000)}`;
    try {
      sessionStorage.setItem(sessionKey, 'true');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  }

  /** Get current storage type for debugging */
  getStorageType(): StorageType {
    return this.storageType;
  }
}

export const storageService = StorageService.getInstance();
export type { IStorageService };
